import type { Block, BlockDefaults, BlockType } from "@aswin.dev/types";
import { createBlock, generateId } from "@aswin.dev/types";
import { cloneSerializable } from "./template-content-clone";

export interface UseBlockActionsOptions {
  addBlock: (
    block: Block,
    targetSectionId?: string,
    columnIndex?: number,
  ) => void;
  removeBlock: (blockId: string) => void;
  updateBlock: (blockId: string, updates: Partial<Block>) => void;
  selectBlock: (blockId: string | null) => void;
  blockDefaults?: BlockDefaults;
}

export interface UseBlockActionsReturn {
  createAndAddBlock: (
    type: BlockType,
    targetSectionId?: string,
    columnIndex?: number,
  ) => Block;
  duplicateBlock: (
    block: Block,
    targetSectionId?: string,
    columnIndex?: number,
  ) => Block;
  deleteBlock: (blockId: string) => void;
  updateBlockProperty: <K extends keyof Block>(
    blockId: string,
    key: K,
    value: Block[K],
  ) => void;
}

export function useBlockActions(
  options: UseBlockActionsOptions,
): UseBlockActionsReturn {
  const { addBlock, removeBlock, updateBlock, selectBlock } = options;

  function createAndAddBlock(
    type: BlockType,
    targetSectionId?: string,
    columnIndex?: number,
  ): Block {
    const block = createBlock(type, options.blockDefaults);
    addBlock(block, targetSectionId, columnIndex);
    selectBlock(block.id);
    return block;
  }

  function duplicateBlock(
    block: Block,
    targetSectionId?: string,
    columnIndex?: number,
  ): Block {
    const cloned = cloneSerializable(block);
    cloned.id = generateId();

    if (cloned.type === "section") {
      cloned.children = cloned.children.map((column) =>
        column.map((child) => {
          const clonedChild = cloneSerializable(child);
          clonedChild.id = generateId();
          return clonedChild;
        }),
      );
    }

    addBlock(cloned, targetSectionId, columnIndex);
    selectBlock(cloned.id);
    return cloned;
  }

  function deleteBlock(blockId: string): void {
    removeBlock(blockId);
  }

  function updateBlockProperty<K extends keyof Block>(
    blockId: string,
    key: K,
    value: Block[K],
  ): void {
    updateBlock(blockId, { [key]: value } as Partial<Block>);
  }

  return {
    createAndAddBlock,
    duplicateBlock,
    deleteBlock,
    updateBlockProperty,
  };
}
